import React, { useContext, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ThemeContext } from "../../hooks/ThemeContext";
import { UserContext } from "../../hooks/UserContext";
import { Moon, Sun } from "lucide-react";
import { FaBars, FaTimes, FaChevronDown, FaSignOutAlt } from "react-icons/fa";

const NAV_LINKS = [
  { path: "/", label: "Accueil" },
  { path: "/services", label: "Services" },
  { path: "/portfolio", label: "Réalisations" },
  { path: "/tarifs", label: "Tarifs" },
  { path: "/contact", label: "Contact" },
];

const G_PRIMARY =
  "linear-gradient(135deg, #f43f5e 0%, #a855f7 50%, #6366f1 100%)";

const Navbar = ({ maintenanceMode = false, userRole = 0 }) => {
  const { user, isAuthenticated, role } = useContext(UserContext);
  const { theme, toggleTheme } = useContext(ThemeContext);
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const isDark = theme === "dark";
  const hideLinks = maintenanceMode && userRole !== 3;

  const toggleDrawer = () => setDrawerOpen((v) => !v);
  const toggleUserMenu = () => setUserMenuOpen((v) => !v);

  const handleLinkClick = () => {
    setUserMenuOpen(false);
    setDrawerOpen(false);
  };

  const logout = () => {
    localStorage.removeItem("access_token");
    window.location.reload();
  };

  // Fermer le menu utilisateur en cliquant hors
  useEffect(() => {
    if (!userMenuOpen) return;
    const handle = (e) => {
      if (!e.target.closest(".user-menu-container")) setUserMenuOpen(false);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [userMenuOpen]);

  return (
    <nav
      className={`sticky top-0 z-50 border-b transition-colors ${
        isDark
          ? "border-white/[0.06] text-white"
          : "bg-white border-gray-100 text-gray-900"
      }`}
      style={{ backgroundColor: isDark ? "#07070a" : undefined }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-6">
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center gap-2.5 flex-shrink-0"
          onClick={handleLinkClick}
        >
          <span
            className="h-2 w-2 rounded-full flex-shrink-0"
            style={{ background: "linear-gradient(135deg, #f43f5e, #a855f7)" }}
          />
          <span
            className="text-base font-black"
            style={{ letterSpacing: "-0.04em" }}
          >
            KRAYNE{" "}
            <span
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: G_PRIMARY }}
            >
              AGENCY
            </span>
          </span>
        </Link>

        {/* Navigation desktop */}
        {!hideLinks && (
          <ul className="hidden lg:flex items-center gap-1 flex-1">
            {NAV_LINKS.map(({ path, label }) => {
              const active = location.pathname === path;
              return (
                <li key={path}>
                  <Link
                    to={path}
                    onClick={handleLinkClick}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                      active
                        ? isDark
                          ? "bg-white/[0.07] text-white"
                          : "bg-gray-100 text-gray-900"
                        : isDark
                          ? "text-gray-400 hover:text-white hover:bg-white/[0.04]"
                          : "text-gray-500 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                  >
                    {label}
                  </Link>
                </li>
              );
            })}
            {role === 3 && (
              <li>
                <Link
                  to="/dashboard"
                  onClick={handleLinkClick}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                    isDark
                      ? "text-rose-400 hover:text-rose-300 hover:bg-rose-500/[0.08]"
                      : "text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                  }`}
                >
                  Admin
                </Link>
              </li>
            )}
          </ul>
        )}

        {/* Actions desktop */}
        {!hideLinks && (
          <div className="hidden lg:flex items-center gap-1.5">
            {/* Toggle thème */}
            <button
              onClick={toggleTheme}
              aria-label="Basculer le thème"
              className={`h-9 w-9 flex items-center justify-center rounded-xl transition-all ${
                isDark
                  ? "text-gray-400 hover:text-white hover:bg-white/[0.06]"
                  : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
              }`}
            >
              {isDark ? (
                <Sun className="w-4 h-4" />
              ) : (
                <Moon className="w-4 h-4" />
              )}
            </button>

            {/* Menu utilisateur connecté */}
            {isAuthenticated ? (
              <div className="relative user-menu-container">
                <button
                  onClick={toggleUserMenu}
                  className={`flex items-center gap-2 h-9 px-2 rounded-xl transition-all ${
                    isDark ? "hover:bg-white/[0.06]" : "hover:bg-gray-100"
                  }`}
                >
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-black text-white flex-shrink-0"
                    style={{
                      background: "linear-gradient(135deg, #f43f5e, #a855f7)",
                    }}
                  >
                    {(user?.nickname || user?.name || "U")[0].toUpperCase()}
                  </div>
                  <span className="hidden md:block text-sm font-semibold leading-none">
                    {user?.nickname || user?.name}
                  </span>
                  <FaChevronDown
                    className={`text-[10px] transition-transform ${
                      userMenuOpen ? "rotate-180" : ""
                    } ${isDark ? "text-gray-500" : "text-gray-400"}`}
                  />
                </button>

                {userMenuOpen && (
                  <div
                    className={`absolute right-0 top-full mt-2 w-60 rounded-2xl shadow-2xl border overflow-hidden z-50 ${
                      isDark ? "border-white/[0.10]" : "border-gray-200"
                    }`}
                    style={{ backgroundColor: isDark ? "#0f0f14" : "#ffffff" }}
                  >
                    <div
                      className={`px-4 py-3 border-b ${
                        isDark ? "border-white/[0.06]" : "border-gray-100"
                      }`}
                    >
                      <p
                        className={`text-sm font-bold ${isDark ? "text-white" : "text-gray-900"}`}
                      >
                        {user?.nickname || user?.name}
                      </p>
                      <p
                        className={`text-xs mt-0.5 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                      >
                        {user?.email}
                      </p>
                    </div>
                    <div className="p-2">
                      <Link
                        to="/espace-client"
                        onClick={handleLinkClick}
                        className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm transition-all ${
                          isDark
                            ? "text-gray-400 hover:text-white hover:bg-white/[0.06]"
                            : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                        }`}
                      >
                        Mon espace client
                      </Link>
                      {role === 3 && (
                        <Link
                          to="/dashboard"
                          onClick={handleLinkClick}
                          className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm transition-all ${
                            isDark
                              ? "text-rose-400 hover:text-rose-300 hover:bg-rose-500/[0.08]"
                              : "text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                          }`}
                        >
                          Administration
                        </Link>
                      )}
                      <button
                        onClick={logout}
                        className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm text-rose-500 hover:bg-rose-500/[0.08] transition-all"
                      >
                        <FaSignOutAlt className="w-3.5 h-3.5" />
                        Déconnexion
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                onClick={handleLinkClick}
                className="h-9 px-4 flex items-center rounded-xl text-sm font-bold text-white transition-all"
                style={{ background: G_PRIMARY }}
              >
                Se connecter
              </Link>
            )}
          </div>
        )}

        {/* Burger mobile */}
        <button
          onClick={toggleDrawer}
          aria-label="Ouvrir le menu"
          className={`lg:hidden h-9 w-9 flex items-center justify-center rounded-xl transition-all ${
            isDark ? "hover:bg-white/[0.06]" : "hover:bg-gray-100"
          }`}
        >
          <FaBars className="w-4 h-4" />
        </button>
      </div>

      {/* Drawer mobile */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-50 lg:hidden"
          role="dialog"
          aria-modal="true"
        >
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={toggleDrawer}
          />
          {/* Panel */}
          <div
            className={`absolute top-0 right-0 h-full w-80 flex flex-col shadow-2xl ${
              isDark
                ? "bg-[#07070a] border-l border-white/[0.06]"
                : "bg-white border-l border-gray-100"
            }`}
            style={{
              backgroundColor: isDark ? "#07070a" : "#ffffff",
            }}
          >
            {/* Header */}
            <div
              className={`flex items-center justify-between px-6 h-16 flex-shrink-0 border-b ${
                isDark ? "border-white/[0.06]" : "border-gray-100"
              }`}
            >
              <Link
                to="/"
                onClick={toggleDrawer}
                className="flex items-center gap-2.5"
              >
                <span
                  className="h-2 w-2 rounded-full flex-shrink-0"
                  style={{
                    background: "linear-gradient(135deg, #f43f5e, #a855f7)",
                  }}
                />
                <span
                  className="font-black text-base"
                  style={{ letterSpacing: "-0.04em" }}
                >
                  KRAYNE{" "}
                  <span
                    className="bg-clip-text text-transparent"
                    style={{ backgroundImage: G_PRIMARY }}
                  >
                    AGENCY
                  </span>
                </span>
              </Link>
              <div className="flex items-center gap-2">
                <button
                  onClick={toggleTheme}
                  aria-label="Basculer le thème"
                  className={`h-8 w-8 flex items-center justify-center rounded-lg transition-all ${
                    isDark ? "hover:bg-white/[0.06]" : "hover:bg-gray-100"
                  }`}
                >
                  {isDark ? (
                    <Sun className="w-4 h-4" />
                  ) : (
                    <Moon className="w-4 h-4" />
                  )}
                </button>
                <button
                  onClick={toggleDrawer}
                  aria-label="Fermer le menu"
                  className={`h-8 w-8 flex items-center justify-center rounded-lg ${
                    isDark ? "hover:bg-white/[0.06]" : "hover:bg-gray-100"
                  }`}
                >
                  <FaTimes />
                </button>
              </div>
            </div>

            {/* Bouton auth mobile */}
            <div className="px-4 pt-6 pb-2 flex-shrink-0">
              {isAuthenticated ? (
                <div className="space-y-1">
                  <div
                    className={`flex items-center gap-3 px-3 py-3 rounded-xl mb-2 ${
                      isDark ? "bg-white/[0.04]" : "bg-gray-50"
                    }`}
                  >
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-black text-white flex-shrink-0"
                      style={{
                        background: "linear-gradient(135deg, #f43f5e, #a855f7)",
                      }}
                    >
                      {(user?.nickname || user?.name || "U")[0].toUpperCase()}
                    </div>
                    <div>
                      <p
                        className={`text-sm font-bold ${isDark ? "text-white" : "text-gray-900"}`}
                      >
                        {user?.nickname || user?.name}
                      </p>
                      <p
                        className={`text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}
                      >
                        {user?.email}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      logout();
                      toggleDrawer();
                    }}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-rose-500 border border-rose-500/20 hover:bg-rose-500/[0.08] transition-colors"
                  >
                    <FaSignOutAlt />
                    Déconnexion
                  </button>
                </div>
              ) : (
                <Link
                  to="/login"
                  onClick={toggleDrawer}
                  className="flex items-center justify-center w-full py-3 rounded-xl text-sm font-bold text-white"
                  style={{ background: G_PRIMARY }}
                >
                  Se connecter
                </Link>
              )}
            </div>

            {/* Liens mobile */}
            <nav className="flex-1 overflow-y-auto px-4 py-2 flex flex-col gap-1">
              {NAV_LINKS.map(({ path, label }) => {
                const active = location.pathname === path;
                return (
                  <Link
                    key={path}
                    to={path}
                    onClick={toggleDrawer}
                    className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                      active
                        ? isDark
                          ? "bg-white/[0.07] text-white"
                          : "bg-gray-100 text-gray-900"
                        : isDark
                          ? "text-gray-400 hover:text-white hover:bg-white/[0.04]"
                          : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                  >
                    {label}
                  </Link>
                );
              })}
              {role === 3 && (
                <Link
                  to="/dashboard"
                  onClick={toggleDrawer}
                  className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                    isDark
                      ? "text-rose-400 hover:text-rose-300 hover:bg-rose-500/[0.08]"
                      : "text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                  }`}
                >
                  Administration
                </Link>
              )}
              {isAuthenticated && (
                <Link
                  to="/espace-client"
                  onClick={toggleDrawer}
                  className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                    isDark
                      ? "text-gray-400 hover:text-white hover:bg-white/[0.04]"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                  }`}
                >
                  Mon espace client
                </Link>
              )}
            </nav>


          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
